import { Button, ButtonColor, ButtonVariant, Checkbox } from "@swivl-mono/ui";
import { FC, useCallback, useEffect, useState } from "react";

import "./CheckboxesModal.scss";

interface Props {
  value: number[];
  options: { label: string; value: number }[];
  onSubmit: (value: number[]) => void;
  onCancel: () => void;
  cancelTitle: string;
  submitTitle: string;
}

interface CheckboxValue {
  label: string;
  value: number;
  checked: boolean;
}

export const CheckboxesModal: FC<Props> = ({ value, options, onSubmit, onCancel, submitTitle, cancelTitle }) => {
  const [checkboxValues, setCheckboxValues] = useState<CheckboxValue[]>([]);

  useEffect(() => {
    if (options.length && Array.isArray(value)) {
      const valueSet = new Set(value);
      const values = options.map(({ label, value }) => ({ label, value, checked: valueSet.has(value) }));
      setCheckboxValues(values);
    }
  }, [value, options]);

  const checkedCount = checkboxValues.filter((item) => item.checked).length;
  const selectAllChecked = checkedCount === 0 ? false : checkedCount === checkboxValues.length ? true : "indeterminate";

  const onSelectAll = () => {
    const shouldSelectAll = checkedCount !== checkboxValues.length;
    const updatedValues = checkboxValues.map((item) => ({
      ...item,
      checked: shouldSelectAll
    }));
    setCheckboxValues(updatedValues);
  };

  const onChecked = useCallback(
    (value: CheckboxValue) => {
      const updatedValues = checkboxValues.map((item) => {
        if (item.value === value.value) {
          item.checked = !item.checked;
        }
        return item;
      });
      setCheckboxValues(updatedValues);
    },
    [checkboxValues]
  );

  const onCancelHandler = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const onSubmitHandler = useCallback(() => {
    const checkedValues = checkboxValues.reduce((acc, item) => {
      if (item.checked) {
        acc.push(item.value);
      }
      return acc;
    }, [] as number[]);
    onSubmit(checkedValues);
  }, [onSubmit, checkboxValues]);

  return (
    <div className="CheckboxesModal">
      <p>Select Member(s)</p>
      <div className="container">
        <div className="selectAll">
          <Checkbox label="Select All" name="selectAll" checked={selectAllChecked} onCheckedChange={onSelectAll} />
        </div>
        <div className="list">
          {checkboxValues.map((val) => (
            <div className="listItem" key={val.value}>
              <Checkbox name={`member-${val.value}`} label={val.label} checked={val.checked} onCheckedChange={() => onChecked(val)} />
            </div>
          ))}
        </div>
      </div>
      <div className="buttons">
        <Button variant={ButtonVariant.GHOST} onClick={onCancelHandler}>
          {cancelTitle}
        </Button>
        <Button color={ButtonColor.ORANGE} onClick={onSubmitHandler}>
          {submitTitle}
        </Button>
      </div>
    </div>
  );
};
